<template>
  <div class="scholar-saved-page flex-col">
    <div class="scholar-saved-shell flex-col">
      <ScholarHeader />

      <main class="scholar-saved-main">
        <div class="scholar-saved-toolbar">
          <button type="button" class="scholar-back-btn" data-track-text="返回检索" aria-label="返回检索" @click="goBackToSearch">
            <i class="el-icon-arrow-left"></i>
            返回检索
          </button>
        </div>

        <section class="scholar-saved-hero">
          <div>
            <span>个人文献库</span>
            <h1>我的收藏</h1>
            <p>集中管理已收藏的 OA 文献，按标题、作者、来源、DOI、标签和备注快速查找。</p>
          </div>
          <div class="scholar-saved-stats">
            <div>
              <strong>{{ formatNumber(totalSaved) }}</strong>
              <em>收藏总数</em>
            </div>
            <div>
              <strong>{{ formatNumber(oaCount) }}</strong>
              <em>开放获取</em>
            </div>
            <div>
              <strong>{{ formatNumber(pdfCount) }}</strong>
              <em>有 PDF</em>
            </div>
          </div>
        </section>

        <section v-if="!personalUserReady" class="scholar-login-card">
          <i class="el-icon-lock"></i>
          <h2>请登录个人账号或子账号</h2>
          <p>收藏文献归属于个人或子账号，机构登录状态下不会展示个人收藏。</p>
          <el-button type="primary" data-track-text="收藏文献登录注册" aria-label="收藏文献登录注册" @click="openLogin">登录 / 注册</el-button>
        </section>

        <template v-else>
          <section class="scholar-saved-search-card">
            <el-input
              v-model="keyword"
              class="scholar-saved-search-input"
              clearable
              prefix-icon="el-icon-search"
              placeholder="搜索标题、作者、来源、DOI、标签或备注"
              @keyup.enter.native="resetAndLoadFirstPage"
            />
            <el-select v-model="tagFilter" class="scholar-saved-tag-filter" clearable placeholder="全部标签">
              <el-option
                v-for="tag in tagOptions"
                :key="tag"
                :label="tag"
                :value="tag"
              />
            </el-select>
            <el-select v-model="sortBy" class="scholar-saved-sort" placeholder="排序">
              <el-option label="最近收藏" value="savedAt" />
              <el-option label="年份最新" value="year" />
              <el-option label="引用最多" value="cited" />
              <el-option label="标题 A-Z" value="title" />
            </el-select>
            <el-button class="scholar-saved-refresh" data-track-text="刷新收藏文献" aria-label="刷新收藏文献" :loading="loading" @click="loadSavedWorks">
              <i class="el-icon-refresh"></i>
              刷新
            </el-button>
            <el-button class="scholar-saved-refresh" type="primary" data-track-text="检索收藏文献" aria-label="检索收藏文献" :loading="loading" @click="resetAndLoadFirstPage">
              检索
            </el-button>
            <el-button class="scholar-saved-export" data-track-text="导出收藏文献CSV" aria-label="导出收藏文献CSV" :disabled="!savedWorks.length" @click="exportFilteredCsv">
              <i class="el-icon-download"></i>
              导出 CSV
            </el-button>
            <el-button class="scholar-saved-copy" data-track-text="复制收藏文献清单" aria-label="复制收藏文献清单" :disabled="!savedWorks.length" @click="copyFilteredList">
              <i class="el-icon-document-copy"></i>
              复制清单
            </el-button>
          </section>

          <section class="scholar-saved-content" v-loading="loading">
            <div class="scholar-saved-head">
              <strong>找到 {{ formatNumber(savedTotal) }} 篇收藏文献</strong>
              <span v-if="keyword || tagFilter">
                已筛选
                <button v-if="keyword" type="button" data-track-text="清除收藏关键词筛选" aria-label="清除收藏关键词筛选" @click="clearKeyword">关键词：{{ keyword }} ×</button>
                <button v-if="tagFilter" type="button" data-track-text="清除收藏标签筛选" aria-label="清除收藏标签筛选" @click="clearTagFilter">标签：{{ tagFilter }} ×</button>
              </span>
            </div>

            <div v-if="pagedSavedWorks.length" class="scholar-saved-list">
              <article
                v-for="item in pagedSavedWorks"
                :key="workKey(item)"
                class="scholar-saved-work"
              >
                <div class="scholar-saved-work-main">
                  <div class="scholar-saved-badges">
                    <span v-if="item.work && item.work.isOa" class="scholar-oa-badge">OA</span>
                    <span v-if="item.work && item.work.pdfUrl" class="scholar-pdf-badge">PDF</span>
                    <span v-if="item.work && item.work.type" class="scholar-type-badge">{{ formatType(item.work.type) }}</span>
                    <span v-if="item.work && item.work.language" class="scholar-type-badge">{{ item.work.language }}</span>
                  </div>
                  <h2 data-track-text="打开收藏文献详情" aria-label="打开收藏文献详情" @click="openWorkDetailPage(item.work)">{{ item.work && item.work.title || 'Untitled work' }}</h2>
                  <p class="scholar-saved-meta">
                    <span>{{ item.work && item.work.publicationYear || '-' }}</span>
                    <span>{{ item.work && item.work.sourceName || '未知来源' }}</span>
                    <span v-if="item.work && item.work.citedByCount !== undefined">引 {{ formatNumber(item.work.citedByCount || 0) }}</span>
                  </p>
                  <p v-if="formatAuthors(item.work && item.work.authors)" class="scholar-saved-authors">
                    {{ formatAuthors(item.work && item.work.authors) }}
                  </p>
                  <p v-if="item.work && item.work.doi" class="scholar-saved-doi">DOI {{ item.work.doi }}</p>
                  <div v-if="item.tags && item.tags.length" class="scholar-saved-tags">
                    <span v-for="tag in item.tags" :key="tag" data-track-text="筛选收藏标签" aria-label="筛选收藏标签" @click="tagFilter = tag">{{ tag }}</span>
                  </div>
                  <p v-if="item.note" class="scholar-saved-note">{{ item.note }}</p>
                </div>
                <div class="scholar-saved-actions">
                  <button type="button" data-track-text="查看收藏文献详情" aria-label="查看收藏文献详情" @click="openWorkDetailPage(item.work)">详情</button>
                  <button type="button" data-track-text="打开收藏文献原文" aria-label="打开收藏文献原文" :disabled="!bestExternalUrl(item.work)" @click="openExternal(bestExternalUrl(item.work))">原文</button>
                  <button type="button" data-track-text="复制收藏文献引用" aria-label="复制收藏文献引用" @click="copyCitation(item.work)">复制引用</button>
                  <button type="button" data-track-text="编辑收藏文献" aria-label="编辑收藏文献" @click="openEditDialog(item)">编辑</button>
                  <button type="button" class="danger" data-track-text="移除收藏文献" aria-label="移除收藏文献" @click="removeSavedWork(item)">移除</button>
                </div>
              </article>
            </div>

            <el-empty
              v-else
              :description="savedTotal ? '没有匹配的收藏文献' : '暂无收藏文献'"
            />

            <div v-if="savedTotal" class="scholar-saved-pagination">
              <el-pagination
                :current-page.sync="page"
                :page-size.sync="pageSize"
                :page-sizes="[10, 20, 50, 100]"
                :total="savedTotal"
                layout="total, sizes, prev, pager, next, jumper"
                @size-change="handlePageSizeChange"
                @current-change="handlePageChange"
              />
            </div>
          </section>
        </template>
      </main>

      <ScholarFooter />
    </div>

    <el-dialog
      class="scholar-saved-edit-dialog"
      :visible.sync="editVisible"
      width="520px"
      title="编辑收藏"
    >
      <el-form label-width="72px">
        <el-form-item label="标签">
          <el-select
            v-model="editForm.tags"
            multiple
            filterable
            allow-create
            default-first-option
            placeholder="添加标签"
          >
            <el-option
              v-for="tag in tagOptionsWithDefaults"
              :key="tag"
              :label="tag"
              :value="tag"
            />
          </el-select>
        </el-form-item>
        <el-form-item label="备注">
          <el-input
            v-model="editForm.note"
            type="textarea"
            :rows="4"
            maxlength="500"
            show-word-limit
            placeholder="记录阅读重点、用途或筛选原因"
          />
        </el-form-item>
      </el-form>
      <div slot="footer">
        <el-button data-track-text="取消编辑收藏文献" aria-label="取消编辑收藏文献" @click="editVisible = false">取消</el-button>
        <el-button type="primary" data-track-text="保存收藏文献编辑" aria-label="保存收藏文献编辑" :loading="saving" @click="submitEdit">保存</el-button>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import { Base64 } from 'js-base64'
import {
  deleteSavedScholarWork,
  pageSavedScholarWorks,
  saveScholarWork
} from '../tools/api/scholar-api'
import { formatCitation, formatWorks } from '../tools/scholar-citation'
import { openExternalWithConfirm } from '../tools/external-link'
import { openScholarRouteInNewTab } from '../tools/scholar-route'
import ScholarHeader from '../components/ScholarHeader.vue'
import ScholarFooter from '../components/ScholarFooter.vue'

export default {
  name: 'ScholarSavedWorks',
  components: {
    ScholarHeader,
    ScholarFooter
  },
  data() {
    return {
      loading: false,
      saving: false,
      savedWorks: [],
      savedTotal: 0,
      totalSaved: 0,
      serverOaCount: 0,
      serverPdfCount: 0,
      serverTagOptions: [],
      keyword: '',
      tagFilter: '',
      sortBy: 'savedAt',
      page: 1,
      pageSize: 10,
      userId: '',
      userInfo: '',
      editVisible: false,
      editItem: null,
      editForm: {
        tags: [],
        note: ''
      }
    }
  },
  computed: {
    personalUserReady() {
      return !!this.userId
    },
    oaCount() {
      return this.serverOaCount
    },
    pdfCount() {
      return this.serverPdfCount
    },
    tagOptions() {
      const tags = new Set()
      ;(this.serverTagOptions || []).forEach(tag => {
        const value = this.cleanTag(tag)
        if (value) {
          tags.add(value)
        }
      })
      ;(this.savedWorks || []).forEach(item => {
        ;(item.tags || []).forEach(tag => {
          const value = this.cleanTag(tag)
          if (value) {
            tags.add(value)
          }
        })
      })
      return Array.from(tags).sort((a, b) => a.localeCompare(b, 'zh-Hans-CN'))
    },
    tagOptionsWithDefaults() {
      const tags = new Set(['OA文献', '待读', '精读', '已引用'])
      this.tagOptions.forEach(tag => tags.add(tag))
      ;(this.editForm.tags || []).forEach(tag => {
        const value = this.cleanTag(tag)
        if (value) {
          tags.add(value)
        }
      })
      return Array.from(tags)
    },
    filteredSavedWorks() {
      return this.savedWorks || []
    },
    pagedSavedWorks() {
      return this.savedWorks || []
    }
  },
  watch: {
    tagFilter() {
      this.resetAndLoadFirstPage()
    },
    sortBy() {
      this.resetAndLoadFirstPage()
    }
  },
  created() {
    this.keyword = this.routeString(this.$route.query.q)
    this.tagFilter = this.routeString(this.$route.query.tag)
    this.syncUserInfoFromStorage()
    if (this.personalUserReady) {
      this.loadSavedWorks()
    }
  },
  mounted() {
    window.addEventListener('message', this.handleAuthMessage)
  },
  beforeDestroy() {
    window.removeEventListener('message', this.handleAuthMessage)
  },
  methods: {
    routeString(value) {
      return Array.isArray(value) ? String(value[0] || '') : String(value || '')
    },
    syncUserInfoFromStorage() {
      const authContext = localStorage.getItem('auth-context')
      this.userId = ''
      this.userInfo = ''
      if (!authContext) {
        return
      }
      try {
        const users = JSON.parse(authContext)
        const user = users.find(item => item && (item.type === 'Person' || item.type === 'Child') && item.name)
        if (!user) {
          return
        }
        this.userId = user.name
        this.userInfo = this.safeDecodeName(user)
      } catch (e) {
        this.userId = ''
        this.userInfo = ''
      }
    },
    safeDecodeName(item) {
      if (!item) {
        return ''
      }
      if (item.realName !== '' && item.realName !== undefined) {
        try {
          return Base64.decode(item.realName)
        } catch (e) {
          return item.name || ''
        }
      }
      return item.name || ''
    },
    async loadSavedWorks() {
      this.syncUserInfoFromStorage()
      if (!this.personalUserReady) {
        this.savedWorks = []
        this.savedTotal = 0
        this.totalSaved = 0
        this.serverOaCount = 0
        this.serverPdfCount = 0
        this.serverTagOptions = []
        return
      }
      this.loading = true
      try {
        const data = await pageSavedScholarWorks({
          q: this.cleanText(this.keyword),
          tag: this.cleanTag(this.tagFilter),
          sort: this.sortBy,
          page: this.page,
          pageSize: this.pageSize
        })
        if (Array.isArray(data)) {
          this.savedWorks = data
          this.savedTotal = data.length
          this.totalSaved = data.length
          this.serverOaCount = data.filter(item => item.work && item.work.isOa).length
          this.serverPdfCount = data.filter(item => item.work && item.work.pdfUrl).length
          this.serverTagOptions = []
        } else {
          this.savedWorks = Array.isArray(data.items) ? data.items : []
          this.savedTotal = Number(data.total || 0)
          this.totalSaved = Number(data.totalSaved || data.total || 0)
          this.serverOaCount = Number(data.oaCount || 0)
          this.serverPdfCount = Number(data.pdfCount || 0)
          this.serverTagOptions = Array.isArray(data.tags) ? data.tags : []
          const maxPage = Math.max(1, Math.ceil(this.savedTotal / this.pageSize))
          if (this.page > maxPage) {
            this.page = maxPage
            await this.loadSavedWorks()
            return
          }
        }
      } catch (e) {
        this.savedWorks = []
        this.savedTotal = 0
        this.totalSaved = 0
        this.serverOaCount = 0
        this.serverPdfCount = 0
        this.serverTagOptions = []
        this.$message.error(this.resolveErrorMessage(e, '收藏列表加载失败'))
      } finally {
        this.loading = false
      }
    },
    handleAuthMessage(e) {
      const data = e && e.data && e.data.data
      if (data === 'params') {
        this.openLogin()
      }
      if (data === 'close' && typeof layer !== 'undefined') {
        layer.closeAll()
        this.syncUserInfoFromStorage()
        if (this.personalUserReady) {
          this.loadSavedWorks()
        }
      }
    },
    openLogin() {
      this.showIframe(process.env.VUE_APP_WEB_LOGIN_URL || '/login')
    },
    showIframe(url) {
      const index = this.$authPopup.open(url)
      if (index === null) {
        this.$router.push(url)
      }
      return index
    },
    goBackToSearch() {
      this.$router.push('/scholar')
    },
    resetAndLoadFirstPage() {
      this.page = 1
      this.loadSavedWorks()
    },
    clearKeyword() {
      this.keyword = ''
      this.resetAndLoadFirstPage()
    },
    clearTagFilter() {
      this.tagFilter = ''
      this.resetAndLoadFirstPage()
    },
    handlePageSizeChange(size) {
      this.pageSize = size
      this.page = 1
      this.loadSavedWorks()
    },
    handlePageChange(page) {
      this.page = page
      this.loadSavedWorks()
      this.$nextTick(() => {
        if (window && window.scrollTo) {
          window.scrollTo({ top: 0, left: 0, behavior: 'smooth' })
        }
      })
    },
    savedSorter(left, right) {
      const workA = left.work || {}
      const workB = right.work || {}
      if (this.sortBy === 'year') {
        return (Number(workB.publicationYear) || 0) - (Number(workA.publicationYear) || 0)
      }
      if (this.sortBy === 'cited') {
        return (Number(workB.citedByCount) || 0) - (Number(workA.citedByCount) || 0)
      }
      if (this.sortBy === 'title') {
        return this.cleanText(workA.title).localeCompare(this.cleanText(workB.title), 'en')
      }
      return this.savedTime(right) - this.savedTime(left)
    },
    savedTime(item) {
      const value = item && (item.updatedAt || item.createdAt || item.savedAt)
      const time = value ? new Date(value).getTime() : 0
      return Number.isFinite(time) ? time : 0
    },
    openWorkDetailPage(work) {
      const ref = this.firstProviderRef(work)
      if (!ref || !ref.provider || !ref.providerId) {
        this.$message.warning('暂无可查看详情')
        return
      }
      const route = this.$router.resolve({
        path: '/scholar/detail',
        query: {
          provider: ref.provider,
          id: ref.providerId
        }
      })
      openScholarRouteInNewTab(route)
    },
    openExternal(url) {
      return openExternalWithConfirm(this, url)
    },
    bestExternalUrl(work) {
      return work && (work.pdfUrl || work.landingPageUrl || this.doiUrl(work) || this.firstProviderUrl(work)) || ''
    },
    openEditDialog(item) {
      if (!item || !item.work) {
        return
      }
      this.editItem = item
      this.editForm = {
        tags: this.cleanTags(item.tags && item.tags.length ? item.tags : ['OA文献']),
        note: item.note || ''
      }
      this.editVisible = true
    },
    async submitEdit() {
      if (!this.editItem || !this.editItem.work || this.saving) {
        return
      }
      this.saving = true
      try {
        const saved = await saveScholarWork(
          this.editItem.work,
          this.cleanTags(this.editForm.tags),
          this.cleanText(this.editForm.note)
        )
        await this.loadSavedWorks()
        this.editVisible = false
        this.$message.success('收藏信息已更新')
      } catch (e) {
        this.$message.error(this.resolveErrorMessage(e, '保存收藏失败'))
      } finally {
        this.saving = false
      }
    },
    async removeSavedWork(item) {
      const work = item && item.work
      if (!work || !work.workKey) {
        return
      }
      const confirmed = await this.$confirm('确认从收藏中移除这篇文献？', '移除收藏', {
        confirmButtonText: '移除',
        cancelButtonText: '取消',
        type: 'warning'
      }).then(() => true).catch(() => false)
      if (!confirmed) {
        return
      }
      try {
        await deleteSavedScholarWork(work.workKey)
        await this.loadSavedWorks()
        this.$message.success('已移除收藏')
      } catch (e) {
        this.$message.error(this.resolveErrorMessage(e, '移除收藏失败'))
      }
    },
    async copyCitation(work) {
      try {
        await this.copyText(formatCitation(work, 'gbt7714'))
        this.$message.success('引用格式已复制')
      } catch (e) {
        this.$message.error('复制失败，请手动选择文本复制')
      }
    },
    async copyFilteredList() {
      try {
        const text = this.filteredSavedWorks.map((item, index) => {
          const work = item.work || {}
          return [
            `${index + 1}. ${work.title || 'Untitled work'}`,
            this.formatAuthors(work.authors),
            [work.sourceName, work.publicationYear].filter(Boolean).join(', '),
            work.doi ? `DOI: ${work.doi}` : '',
            item.note ? `备注: ${item.note}` : ''
          ].filter(Boolean).join('\n')
        }).join('\n\n')
        await this.copyText(text)
        this.$message.success('收藏清单已复制')
      } catch (e) {
        this.$message.error('复制失败，请稍后重试')
      }
    },
    exportFilteredCsv() {
      const works = this.filteredSavedWorks.map(item => item.work).filter(Boolean)
      if (!works.length) {
        this.$message.warning('当前没有可导出的收藏文献')
        return
      }
      this.downloadText(`\uFEFF${formatWorks(works, 'csv', this.formatType)}`, 'scholar-saved-works.csv', 'text/csv;charset=utf-8')
      this.$message.success(`已导出 ${works.length} 篇收藏文献`)
    },
    async copyText(text) {
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(text || '')
        return
      }
      const textarea = document.createElement('textarea')
      textarea.value = text || ''
      textarea.setAttribute('readonly', 'readonly')
      textarea.style.position = 'fixed'
      textarea.style.left = '-9999px'
      document.body.appendChild(textarea)
      textarea.select()
      const success = document.execCommand('copy')
      document.body.removeChild(textarea)
      if (!success) {
        throw new Error('copy failed')
      }
    },
    downloadText(content, filename, type) {
      const blob = new Blob([content], { type: type || 'text/plain;charset=utf-8' })
      const url = window.URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.download = filename
      link.style.display = 'none'
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      window.setTimeout(() => window.URL.revokeObjectURL(url), 1000)
    },
    workKey(item) {
      const work = item && item.work ? item.work : item
      return work && (work.workKey || work.doi || this.firstProviderId(work) || work.title) || ''
    },
    firstProviderRef(work) {
      return work && work.providerRefs && work.providerRefs.length ? work.providerRefs[0] : null
    },
    firstProviderId(work) {
      const ref = this.firstProviderRef(work)
      return ref ? ref.providerId : ''
    },
    firstProviderUrl(work) {
      const ref = this.firstProviderRef(work)
      return ref ? ref.url : ''
    },
    doiUrl(work) {
      return work && work.doi ? `https://doi.org/${work.doi}` : ''
    },
    formatAuthors(authors, limit = 6) {
      const names = (authors || []).map(item => item && item.name).filter(Boolean)
      if (names.length <= limit) {
        return names.join('、')
      }
      return `${names.slice(0, limit).join('、')} 等 ${names.length} 位`
    },
    cleanText(value) {
      return String(value === null || value === undefined ? '' : value).replace(/\s+/g, ' ').trim()
    },
    cleanTag(value) {
      return this.cleanText(value).replace(/[，,;；]+/g, ' ').trim().slice(0, 24)
    },
    cleanTags(values) {
      const tags = Array.isArray(values) ? values : []
      const seen = new Set()
      return tags
        .map(tag => this.cleanTag(tag))
        .filter(tag => {
          if (!tag) {
            return false
          }
          const key = tag.toLowerCase()
          if (seen.has(key)) {
            return false
          }
          seen.add(key)
          return true
        })
        .slice(0, 8)
    },
    formatNumber(value) {
      const number = Number(value || 0)
      return number.toLocaleString()
    },
    formatType(type) {
      const typeMap = {
        article: '论文',
        preprint: '预印本',
        'book-chapter': '图书章节',
        book: '书籍',
        'proceedings-article': '会议论文',
        'conference-paper': '会议论文',
        'conference-abstract': '会议摘要',
        review: '综述',
        dissertation: '学位论文',
        dataset: '数据集',
        report: '报告',
        editorial: '社论',
        letter: '通信',
        paratext: '附属材料',
        'peer-review': '同行评议',
        erratum: '勘误',
        software: '软件',
        standard: '标准'
      }
      return typeMap[type] || type || '文献'
    },
    resolveErrorMessage(error, fallback) {
      if (error && error.response && error.response.data) {
        const data = error.response.data
        return data.message || data.detail || data.error || fallback || '操作失败，请稍后重试'
      }
      return (error && error.message) || fallback || '操作失败，请稍后重试'
    }
  }
}
</script>

<style scoped lang="less">
.scholar-saved-page {
  min-height: 100vh;
  background: var(--bg-page);
  color: var(--foreground);
}

.scholar-saved-shell {
  min-height: 100vh;
}

.scholar-saved-main {
  position: relative;
  min-width: 1200px;
  min-height: calc(100vh - 180px);
  padding: 32px 24px 64px;
  box-sizing: border-box;
  background: linear-gradient(180deg, var(--primary-light) 0%, var(--bg-page) 320px);
}

.scholar-saved-toolbar,
.scholar-saved-hero,
.scholar-saved-search-card,
.scholar-saved-content,
.scholar-login-card {
  width: 100%;
  margin-left: auto;
  margin-right: auto;
  box-sizing: border-box;
}

.scholar-saved-toolbar {
  margin-bottom: 16px;
}

.scholar-back-btn {
  height: 34px;
  padding: 0 14px;
  border: 1px solid var(--border);
  border-radius: 4px;
  background: #ffffff;
  color: var(--muted-foreground);
  font-size: 14px;
  cursor: pointer;
}

.scholar-back-btn:hover,
.scholar-back-btn:focus {
  border-color: var(--primary);
  color: var(--primary);
}

.scholar-saved-hero {
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 32px;
  margin-bottom: 18px;
}

.scholar-saved-hero span {
  display: inline-flex;
  margin-bottom: 8px;
  color: var(--primary);
  font-size: 13px;
  font-weight: 700;
}

.scholar-saved-hero h1 {
  margin: 0;
  color: var(--foreground);
  font-size: 30px;
  line-height: 40px;
}

.scholar-saved-hero p {
  margin: 10px 0 0;
  color: var(--muted-foreground);
  font-size: 14px;
  line-height: 22px;
}

.scholar-saved-stats {
  display: grid;
  grid-template-columns: repeat(3, 120px);
  gap: 10px;
}

.scholar-saved-stats div {
  min-height: 72px;
  padding: 12px;
  border: 1px solid var(--border);
  border-radius: 6px;
  background: rgba(255, 255, 255, 0.86);
  box-sizing: border-box;
}

.scholar-saved-stats strong {
  display: block;
  color: var(--foreground);
  font-size: 24px;
  line-height: 30px;
}

.scholar-saved-stats em {
  display: block;
  color: var(--muted-fg-light);
  font-size: 12px;
  font-style: normal;
}

.scholar-login-card,
.scholar-saved-search-card,
.scholar-saved-content {
  border: 1px solid var(--border);
  border-radius: 6px;
  background: #ffffff;
  box-shadow: 0 8px 22px rgba(35, 88, 120, 0.08);
}

.scholar-login-card {
  min-height: 280px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  color: var(--muted-foreground);
  text-align: center;
}

.scholar-login-card i {
  color: var(--primary);
  font-size: 42px;
}

.scholar-login-card h2 {
  margin: 16px 0 8px;
  color: var(--foreground);
  font-size: 22px;
}

.scholar-login-card p {
  margin: 0 0 18px;
  font-size: 14px;
}

.scholar-saved-search-card {
  display: grid;
  grid-template-columns: minmax(0, 1fr) 160px 130px auto auto auto auto;
  align-items: center;
  gap: 10px;
  margin-bottom: 18px;
  padding: 16px;
}

.scholar-saved-search-input /deep/ .el-input__inner,
.scholar-saved-tag-filter /deep/ .el-input__inner,
.scholar-saved-sort /deep/ .el-input__inner {
  height: 36px;
  border-color: var(--border);
  border-radius: 4px;
}

.scholar-saved-refresh,
.scholar-saved-export,
.scholar-saved-copy {
  height: 36px;
  padding: 0 12px;
  border-radius: 4px;
}

/deep/ .el-button--primary {
  background: var(--primary);
  border-color: var(--primary);
}
/deep/ .el-button--primary:hover,
/deep/ .el-button--primary:focus {
  background: var(--primary-dark);
  border-color: var(--primary-dark);
}

.scholar-saved-content {
  padding: 18px;
  min-height: 520px;
}

.scholar-saved-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 14px;
}

.scholar-saved-head strong {
  color: var(--foreground);
  font-size: 18px;
}

.scholar-saved-head span {
  display: inline-flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 6px;
  color: var(--muted-fg-light);
  font-size: 13px;
}

.scholar-saved-head button {
  height: 24px;
  padding: 0 8px;
  border: 1px solid var(--border);
  border-radius: 12px;
  background: var(--primary-alpha-08);
  color: var(--primary);
  font-size: 12px;
  cursor: pointer;
}

.scholar-saved-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.scholar-saved-work {
  display: grid;
  grid-template-columns: minmax(0, 1fr) 160px;
  gap: 20px;
  padding: 16px;
  border: 1px solid var(--border);
  border-radius: 6px;
  background: #ffffff;
  box-sizing: border-box;
}

.scholar-saved-work:hover {
  border-color: var(--border);
  box-shadow: 0 6px 18px rgba(35, 88, 120, 0.08);
}

.scholar-saved-badges,
.scholar-saved-tags {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 6px;
}

.scholar-oa-badge,
.scholar-pdf-badge,
.scholar-type-badge,
.scholar-saved-tags span {
  display: inline-flex;
  align-items: center;
  height: 22px;
  padding: 0 8px;
  border-radius: 11px;
  background: var(--primary-alpha-08);
  color: var(--primary);
  font-size: 12px;
  line-height: 22px;
}

.scholar-oa-badge {
  background: #dff9ec;
  color: #12a466;
}

.scholar-pdf-badge {
  background: var(--primary);
  color: #ffffff;
}

.scholar-saved-work h2 {
  margin: 8px 0 6px;
  color: var(--foreground);
  font-size: 18px;
  line-height: 26px;
  cursor: pointer;
}

.scholar-saved-work h2:hover {
  color: var(--primary);
}

.scholar-saved-meta,
.scholar-saved-authors,
.scholar-saved-doi,
.scholar-saved-note {
  margin: 0 0 7px;
  color: var(--muted-foreground);
  font-size: 13px;
  line-height: 21px;
}

.scholar-saved-meta {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
}

.scholar-saved-doi {
  color: var(--primary);
}

.scholar-saved-tags {
  margin-top: 8px;
}

.scholar-saved-tags span {
  cursor: pointer;
}

.scholar-saved-note {
  margin-top: 10px;
  padding: 10px;
  border-left: 3px solid var(--primary);
  border-radius: 4px;
  background: var(--primary-alpha-08);
  color: var(--muted-foreground);
}

.scholar-saved-actions {
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: 8px;
}

.scholar-saved-actions button {
  height: 30px;
  border: 1px solid var(--border);
  border-radius: 4px;
  background: #ffffff;
  color: var(--primary);
  font-size: 13px;
  cursor: pointer;
}

.scholar-saved-actions button:hover:not(:disabled),
.scholar-saved-actions button:focus:not(:disabled) {
  border-color: var(--primary);
  background: var(--primary-alpha-08);
}

.scholar-saved-actions button:disabled {
  color: var(--muted-fg-light);
  cursor: not-allowed;
}

.scholar-saved-actions button.danger {
  color: #d25252;
}

.scholar-saved-actions button.danger:hover,
.scholar-saved-actions button.danger:focus {
  border-color: #e8bcbc;
  background: #fff5f5;
}

.scholar-saved-pagination {
  display: flex;
  justify-content: flex-end;
  margin-top: 18px;
}

.scholar-saved-edit-dialog /deep/ .el-select {
  width: 100%;
}

@media (max-width: 1280px) {
  .scholar-saved-toolbar,
  .scholar-saved-hero,
  .scholar-saved-search-card,
  .scholar-saved-content,
  .scholar-login-card {
    width: calc(100vw - 80px);
  }
}

@media (max-width: 768px) {
  .scholar-saved-page,
  .scholar-saved-shell,
  .scholar-saved-main {
    width: 100%;
    min-width: 0;
    max-width: 100%;
    box-sizing: border-box;
  }

  .scholar-saved-page,
  .scholar-saved-shell {
    overflow-x: hidden;
  }

  .scholar-saved-main {
    min-height: calc(100vh - 116px);
    padding: 16px 12px calc(32px + env(safe-area-inset-bottom));
    background: linear-gradient(180deg, var(--primary-light) 0%, var(--bg-page) 260px);
  }

  .scholar-saved-toolbar,
  .scholar-saved-hero,
  .scholar-saved-search-card,
  .scholar-saved-content,
  .scholar-login-card {
    width: 100%;
    min-width: 0;
    max-width: 100%;
    margin-left: 0;
    margin-right: 0;
  }

  .scholar-saved-toolbar {
    margin-bottom: 12px;
  }

  .scholar-back-btn {
    min-height: 36px;
  }

  .scholar-saved-hero {
    display: block;
    margin-bottom: 14px;
  }

  .scholar-saved-hero h1 {
    font-size: 25px;
    line-height: 34px;
  }

  .scholar-saved-hero p {
    margin-top: 6px;
    line-height: 20px;
  }

  .scholar-saved-stats {
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: 8px;
    width: 100%;
    margin-top: 14px;
  }

  .scholar-saved-stats div {
    min-width: 0;
    min-height: 64px;
    padding: 10px 8px;
    text-align: center;
  }

  .scholar-saved-stats strong {
    overflow: hidden;
    font-size: 20px;
    line-height: 26px;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .scholar-login-card {
    min-height: 240px;
    padding: 24px 16px;
  }

  .scholar-login-card h2 {
    font-size: 20px;
  }

  .scholar-login-card p {
    line-height: 21px;
  }

  .scholar-saved-search-card {
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 8px;
    margin-bottom: 12px;
    padding: 12px;
  }

  .scholar-saved-search-input {
    grid-column: 1 / -1;
  }

  .scholar-saved-search-input,
  .scholar-saved-tag-filter,
  .scholar-saved-sort,
  .scholar-saved-refresh,
  .scholar-saved-export,
  .scholar-saved-copy {
    width: 100%;
    min-width: 0;
  }

  .scholar-saved-search-card /deep/ .el-button {
    width: 100%;
    min-width: 0;
    margin-left: 0;
    padding-left: 8px;
    padding-right: 8px;
  }

  .scholar-saved-content {
    min-height: 360px;
    padding: 12px;
  }

  .scholar-saved-head {
    align-items: flex-start;
    flex-direction: column;
    gap: 8px;
    margin-bottom: 12px;
  }

  .scholar-saved-head strong {
    font-size: 16px;
  }

  .scholar-saved-head span {
    width: 100%;
  }

  .scholar-saved-work {
    grid-template-columns: minmax(0, 1fr);
    gap: 14px;
    padding: 14px;
  }

  .scholar-saved-work-main {
    min-width: 0;
  }

  .scholar-saved-work h2 {
    overflow-wrap: anywhere;
    font-size: 16px;
    line-height: 24px;
  }

  .scholar-saved-authors,
  .scholar-saved-doi,
  .scholar-saved-note {
    overflow-wrap: anywhere;
    word-break: break-word;
  }

  .scholar-saved-actions {
    flex-direction: row;
    flex-wrap: wrap;
    gap: 8px;
  }

  .scholar-saved-actions button {
    flex: 1 1 calc(50% - 4px);
    min-width: 0;
    min-height: 34px;
  }

  .scholar-saved-pagination {
    width: 100%;
    justify-content: center;
    overflow-x: auto;
    padding-bottom: 2px;
  }

  .scholar-saved-pagination /deep/ .el-pagination {
    display: flex;
    align-items: center;
    justify-content: center;
    min-width: 0;
    padding: 2px 0;
    white-space: nowrap;
  }

  .scholar-saved-pagination /deep/ .el-pagination__sizes,
  .scholar-saved-pagination /deep/ .el-pagination__jump {
    display: none;
  }

  .scholar-saved-pagination /deep/ .el-pagination__total {
    margin-right: 6px;
  }

  .scholar-saved-pagination /deep/ .btn-prev,
  .scholar-saved-pagination /deep/ .btn-next,
  .scholar-saved-pagination /deep/ .el-pager li {
    min-width: 28px;
    padding: 0 4px;
  }

  .scholar-saved-edit-dialog /deep/ .el-dialog {
    width: calc(100vw - 24px) !important;
    max-width: 520px;
    margin: 20px auto 0 !important;
  }

  .scholar-saved-edit-dialog /deep/ .el-dialog__header {
    padding: 16px 18px 10px;
  }

  .scholar-saved-edit-dialog /deep/ .el-dialog__body {
    max-height: calc(100vh - 190px);
    padding: 14px 16px;
    overflow-y: auto;
  }

  .scholar-saved-edit-dialog /deep/ .el-dialog__footer {
    padding: 10px 16px calc(14px + env(safe-area-inset-bottom));
  }

  .scholar-saved-edit-dialog /deep/ .el-form-item__label {
    float: none;
    width: auto !important;
    padding: 0 0 6px;
    line-height: 24px;
  }

  .scholar-saved-edit-dialog /deep/ .el-form-item__content {
    margin-left: 0 !important;
  }
}

@media (max-width: 380px) {
  .scholar-saved-main {
    padding-left: 8px;
    padding-right: 8px;
  }

  .scholar-saved-search-card,
  .scholar-saved-content {
    padding: 10px;
  }

  .scholar-saved-stats {
    gap: 5px;
  }

  .scholar-saved-stats div {
    padding-left: 4px;
    padding-right: 4px;
  }

  .scholar-saved-stats strong {
    font-size: 18px;
  }
}
</style>
